import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { newToastEvent } from "../../utils/ToastUtil";
import useSharedPage, { pagePasswordAtom } from "./useSharedPage";
import LoadingView from "../SharedViewLayout/LoadingView";
import PasswordProtectedView from "../SharedViewLayout/PasswordProtectedView";
import SharedScreenAPI from "../../api/api/SharedScreenAPI";
import { Header } from "../../components/PageView/PageViewHeader";
import AppNavbar from "../../components/AppNavbar/AppNavbar";
import { HeaderWrapper } from "../../views/pages/PageView";
import useSharedPageView from "./useSharedPageView";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";
import PageViewRenderer from "../../components/PageView/PageViewRenderer";
import NotFoundView from "../SharedViewLayout/NotFoundView";

export default function SharedPageLayout() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const setPagePasswords = useSetAtom(pagePasswordAtom);
  const setSchema = useSetAtom(setSchemaAtom);

  const client = useQueryClient();
  const navigate = useNavigate();

  const { sharedPageID, tenantID, viewID } = useParams();
  const { sharedPage, sharedPageLoading } = useSharedPage(
    tenantID!,
    sharedPageID!
  );

  const success = sharedPage?.success;
  const requiresPassword = sharedPage?.requiresPassword;
  const firstViewId = sharedPage?.page?.Views?.[0]?.id;

  const { sharedPageView, sharedPageViewLoading } = useSharedPageView(
    tenantID!,
    sharedPageID!,
    viewID!,
    sharedPageLoading || !success
  );

  // Setup first view ID - An inital shared page link does not have one included.
  useEffect(() => {
    if (sharedPageLoading) return;

    if (!viewID && firstViewId) {
      navigate(`${window.location.pathname}/${firstViewId}`);
    }
  }, [sharedPageLoading, firstViewId]);

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      if (
        err?.config?.url?.includes("/getSharedPage/") &&
        err?.response?.status === 400 &&
        err?.response?.data === "Bad Request"
      ) {
        navigate(`/login?r=${window.location.href}`);
        newToastEvent("error", "Please login to access that page.");
      }

      return Promise.reject(err);
    }
  );

  if (sharedPageLoading || (!viewID && firstViewId)) {
    return <LoadingView />;
  }

  if (!success && requiresPassword) {
    return (
      <PasswordProtectedView
        onSubmit={updatePassword}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (!success) return <NotFoundView />;

  return (
    <div>
      <HeaderWrapper loading={false}>
        <div className="mt-2 mt-sm-3 d-flex justify-content-between align-items-center mb-1">
          <div>
            <Header page={sharedPage?.page} />
          </div>
        </div>

        {/* @ts-ignore */}
        <AppNavbar
          tabs={sharedPage?.page?.Views?.map((v: any) => ({
            title: v.Name,
            activeValue: v.id,
          }))}
          activeTab={viewID}
          onSelect={(viewID: string) => {
            const existingData = client.getQueryData<any>([
              "shared-page-view",
              sharedPageID,
              viewID,
            ]);

            if (existingData?.schema) {
              setSchema(existingData?.schema);
            }

            navigate(`/shared-page/${tenantID}/${sharedPageID}/${viewID}`);
          }}
          dynamic={false}
        />
      </HeaderWrapper>
      {sharedPageViewLoading && <LoadingView />}
      {!sharedPageViewLoading && sharedPageView?.view && (
        <PageViewRenderer
          page={{
            Name: sharedPageView?.view?.Name,
            Views: [sharedPageView?.view],
            Description: "",
            IconURL: "",
            Privacy: "public",
            Owner: "",
            Icon: "",
            Members: [],
            _id: "",
            id: "",
            Created: "",
            CreatedBy: "",
            LastUpdated: "",
            LastUpdatedBy: "",
            Tags: [],
          }}
          pageLoading={false}
          view={sharedPageView?.view}
          viewLoading={false}
          viewID={sharedPageView?.view?._id}
          record={sharedPageView.record}
          showInsights={true}
          commentsWidget={false}
        />
      )}
      {!sharedPageViewLoading && !sharedPageView?.view && <NotFoundView />}
    </div>
  );

  async function updatePassword(password: string) {
    if (!password) return;

    setError("");
    setSubmitting(true);

    setPagePasswords((prev) => ({
      ...prev,
      [`${tenantID}-${sharedPageID}`]: password,
    }));

    try {
      const result = await SharedScreenAPI.getSharedPage(
        tenantID!,
        sharedPageID!,
        password
      );

      const data = result.data.data;

      if (data.success === false) {
        setError("Invalid password provided.");
      } else if (data.success === true) {
        client.setQueryData(["shared-page", sharedPageID], data);
      }
    } catch {
      setError("An unexpected error");
    } finally {
      setSubmitting(false);
    }
  }
}
